import { KeyIcon, Loader2, MailIcon } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/app/components/ui/button";
import { Dialog, DialogContent } from "@/app/components/ui/dialog.jsx";
import { useAuth } from "@/app/context/Auth/AuthContext.jsx";
import { useCompanyClaim } from "@/app/hooks/useCompanyClaim.js";

const SUCCESS_MESSAGE = "You are logged out and can now manage any company dashboard.";
const ACTION_BUTTON_BASE_CLASSNAME =
	"inline-flex! h-14! flex-1! items-center! justify-center! rounded-xl! px-6! font-['Inter',Helvetica] text-base! font-semibold! shadow-none!";
const CANCEL_BUTTON_CLASSNAME = `${ACTION_BUTTON_BASE_CLASSNAME} border! border-gray-300! bg-white! text-anima-dark-text! hover:bg-gray-50! hover:text-anima-dark-text!`;
const VERIFY_BUTTON_CLASSNAME = `${ACTION_BUTTON_BASE_CLASSNAME} bg-primary-700! text-white! hover:bg-primary-700/90! disabled:bg-primary-700! disabled:text-white! disabled:opacity-60!`;

export function AdminDashboardAccessModal({ open, onOpenChange, companyId, email }) {
	const navigate = useNavigate();
	const { logout, setSession } = useAuth();
	const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
	const inputRefs = useRef([]);
	const { requestClaimOTP, verifyClaimOTP, isLoading } = useCompanyClaim();

	const resetModal = () => {
		setVerificationCode(["", "", "", "", "", ""]);
	};

	const handleClose = () => {
		onOpenChange(false);
		setTimeout(resetModal, 300);
	};

	const handleCodeChange = (index, value) => {
		let nextValue = value;
		if (nextValue.length > 1) {
			nextValue = nextValue[0];
		}

		if (!/^\d*$/.test(nextValue)) return;

		const nextCode = [...verificationCode];
		nextCode[index] = nextValue;
		setVerificationCode(nextCode);

		if (nextValue && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index, event) => {
		if (event.key === "Backspace" && !verificationCode[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (event) => {
		event.preventDefault();
		const pastedData = event.clipboardData.getData("text").slice(0, 6);
		if (!/^\d+$/.test(pastedData)) return;

		const nextCode = [...verificationCode];
		pastedData.split("").forEach((char, index) => {
			if (index < 6) {
				nextCode[index] = char;
			}
		});
		setVerificationCode(nextCode);

		const nextEmptyIndex = nextCode.findIndex((digit) => !digit);
		if (nextEmptyIndex === -1) {
			inputRefs.current[5]?.focus();
			return;
		}

		inputRefs.current[nextEmptyIndex]?.focus();
	};

	const handleResend = async () => {
		if (!email) return;

		await requestClaimOTP({
			companyId,
			email,
		});
	};

	const handleVerify = async () => {
		const otp = verificationCode.join("");
		if (otp.length !== 6 || !email) return;

		const response = await verifyClaimOTP({
			companyId,
			email,
			otp,
		});

		if (!response?.token) {
			return;
		}

		logout();
		setSession({ token: response.token, expiresAt: response.expiresAt });
		onOpenChange(false);
		resetModal();
		navigate("/companies?page=1&status=hiring", {
			replace: true,
			state: {
				dashboardAccessMessage: SUCCESS_MESSAGE,
			},
		});
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[800px] bg-white p-12">
				<div className="flex flex-col items-center gap-8">
					<div className="w-[60px] h-[60px] bg-anima-light-blue-bg rounded-3xl flex items-center justify-center">
						<KeyIcon className="w-8 h-8 text-primary-700" strokeWidth={1.5} />
					</div>

					<div className="flex flex-col gap-3 text-center">
						<h2 className="text-[32px] font-bold text-anima-dark-text font-['Plus_Jakarta_Sans',Helvetica] leading-tight">
							Verify ops dashboard access
						</h2>
						<p className="text-base text-[#6c757d] font-['Inter',Helvetica] leading-relaxed">
							We&apos;ve sent a 6-digit code to <span className="font-semibold text-anima-dark-text">{email}</span>
						</p>
					</div>

					<div className="w-full rounded-2xl border border-gray-300 bg-[#f8fbff] px-5 py-4 flex items-center gap-3">
						<div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-gray-300">
							<MailIcon className="w-5 h-5 text-primary-700" />
						</div>
						<div className="text-left">
							<p className="text-sm font-medium text-anima-dark-text">Verification email</p>
							<p className="text-sm text-[#6c757d]">{email}</p>
						</div>
					</div>

					<div className="flex items-center justify-center gap-3 w-full">
						{verificationCode.map((digit, index) => (
							<input
								key={index}
								ref={(element) => (inputRefs.current[index] = element)}
								type="text"
								inputMode="numeric"
								maxLength={1}
								value={digit}
								onChange={(event) => handleCodeChange(index, event.target.value)}
								onKeyDown={(event) => handleKeyDown(index, event)}
								onPaste={index === 0 ? handlePaste : undefined}
								disabled={isLoading}
								className="w-16 h-16 text-center text-xl font-semibold bg-white border-2 border-gray-300 rounded-2xl font-['Inter',Helvetica] text-anima-dark-text focus:outline-hidden focus:ring-2 focus:ring-primary-700 focus:border-primary-700 disabled:opacity-50"
							/>
						))}
					</div>

					<p className="text-base text-[#6c757d] font-['Inter',Helvetica]">
						Didn&apos;t get a code?{" "}
						<button
							type="button"
							onClick={handleResend}
							disabled={isLoading}
							className="text-primary-700 underline hover:text-[#0856b3] font-medium"
						>
							Click to resend
						</button>
						.
					</p>

					<div className="admin-dashboard-access-modal__actions flex items-center gap-4 w-full pt-4">
						<Button
							type="button"
							onClick={handleClose}
							variant="outline"
							disabled={isLoading}
							className={CANCEL_BUTTON_CLASSNAME}
						>
							Cancel
						</Button>
						<Button
							type="button"
							onClick={handleVerify}
							disabled={verificationCode.join("").length !== 6 || isLoading}
							className={VERIFY_BUTTON_CLASSNAME}
						>
							{isLoading ? (
								<>
									<Loader2 className="w-5 h-5 animate-spin" />
									Verifying...
								</>
							) : (
								"Verify & Continue"
							)}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
